/**
 * `<page-datasources>` — the instance's catalogs, routed at
 * `/instances/:id/datasources`.
 *
 * Implements `SC-F400` (the catalog identity: `name` **is** the DuckDB catalog
 * name every `resources.catalogs` pattern matches), `SC-F405` (a stored
 * credential is shown as *existing* and never as a value — `bn-kv`'s redacted
 * state, which has no copy button because there is nothing to copy),
 * `SC-F407` (the honest label: **Validate configuration**, never "Test
 * connection", with the check's own `checked` / `not_checked` / `notice` prose
 * rendered verbatim, and the two badged-not-enforced field families named from
 * the form schema), `SC-F409` / `SC-F607` (a datasource change is **always**
 * restart-class, stated here — where the change is made — and not only in the
 * publish dialog), `SC-F410` (per-catalog health from the instance's own
 * `/admin/datasources`, refreshed on the instance's own probe interval so the UI
 * never implies finer resolution than exists, and never rendering a stale reading
 * as current), `SC-F411` (the schema cache's staleness, stated rather than
 * implied), `SC-F413` (the delete guard: the referencing objects by kind, name
 * and pattern, and a forced delete that carries the exact acknowledgement the API
 * enforces), `SC-F415` (files on the state volume, because two of the six driver
 * types are otherwise unreachable in a hosted deployment) and `SC-F416` (the
 * sample catalog, badged as sample data on every surface that shows it).
 *
 * Stories: `SC-S400`–`SC-S406`, `SC-S408`–`SC-S411`.
 *
 * The one thing this page must never do is imply that a datasource works because
 * it saved. Nothing here opens a socket: the wizard's check is spec-level, the
 * health panel is the instance's own observation, and connectivity is proven only
 * once the instance runs.
 */

import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
} from "../components/index.js";

import { ApiError, errorDetail, parseError } from "../api/errors.js";
import {
  attachSampleCatalog,
  checkDatasources,
  deleteDatasource,
  deleteFile,
  listDatasources,
  listFiles,
  placeFile,
  removeSampleCatalog,
  FORCE_ACKNOWLEDGEMENT,
  type CatalogReference,
  type CheckReport,
  type DataSource,
  type DataSourceListing,
  type FileListing,
  type Health,
  type NotEnforcedField,
} from "../api/datasources.js";
import { hrefFor, navigate } from "../nav.js";
// `SC-F412`. The browser lives on this page rather than at a route of its own,
// because the question it answers — what is actually in these catalogs? — is the
// question this list raises and cannot answer.
import "./schema-browser.js";

/** The health words, each with the tone it is allowed to carry. `unknown` is a
 * first-class value: an unprobed catalog must not read as healthy. */
const HEALTH: Record<string, { label: string; variant: BnBadgeVariant }> = {
  healthy: { label: "Healthy", variant: "success" },
  unhealthy: { label: "Unhealthy", variant: "error" },
  unknown: { label: "Unknown", variant: "neutral" },
  degraded: { label: "Degraded", variant: "warning" },
  unavailable: { label: "Unavailable", variant: "warning" },
};

const COLUMNS: BnColumn[] = [
  { key: "catalog", label: "Catalog" },
  { key: "type", label: "Driver", width: "1px" },
  { key: "health", label: "Health", width: "1px" },
  { key: "target", label: "Target" },
  { key: "credential", label: "Credential", width: "1px" },
  { key: "actions", label: "", width: "1px", align: "right" },
];

const FILE_COLUMNS: BnColumn[] = [
  { key: "name", label: "File" },
  { key: "size", label: "Size", width: "1px", align: "right" },
  { key: "digest", label: "SHA-256", mono: true },
  { key: "used", label: "Used by", width: "1px" },
  { key: "actions", label: "", width: "1px", align: "right" },
];

@customElement("page-datasources")
export class PageDatasources extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  @state() private listing: DataSourceListing | null = null;
  @state() private loading = true;
  @state() private failure: ApiError | null = null;

  /** Placed files (`SC-F415`). A failure here costs the picker and the upload
   * control, not the page. */
  @state() private files: FileListing | null = null;
  @state() private filesFailure: ApiError | null = null;
  @state() private uploadName = "";
  @state() private uploadFile: File | null = null;
  @state() private uploading = false;

  /** The configuration check (`SC-F407`). Held on screen rather than toasted:
   * the interesting half is what it did **not** check. */
  @state() private check: CheckReport | null = null;
  @state() private checking = false;
  @state() private checkFailure: ApiError | null = null;

  /** The delete ceremony (`SC-F413`). */
  @state() private deleteTarget: DataSource | null = null;
  @state() private deleting = false;
  @state() private deleteFailure: ApiError | null = null;
  @state() private references: CatalogReference[] = [];
  @state() private forceAcknowledged = false;

  @state() private sampleBusy = false;

  /** The health poll, on the instance's own probe interval. */
  private healthTimer = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 26rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 70ch;
    }
    .head-actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 74ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .name {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-weight: 600;
      color: var(--bino-primary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .name:hover {
      text-decoration: underline;
    }
    .name:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .mono {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      overflow-wrap: anywhere;
    }
    .cell-actions {
      display: flex;
      gap: var(--bino-space-xs);
      justify-content: flex-end;
    }
    /* An error string from a third-party driver: escaped, monospace, copyable,
       and never rendered as markup (SC-S405). It scrolls inside its own box so a
       one-line stack trace cannot make the page scroll sideways. */
    .driver-error {
      margin: var(--bino-space-xs) 0 0;
      padding: var(--bino-space-xs) var(--bino-space-sm);
      max-width: 60ch;
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-error-bg);
      color: var(--bino-text);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      white-space: pre-wrap;
      overflow-x: auto;
    }
    ul.plain {
      margin: 0;
      padding-left: 1.1rem;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    ul.plain li + li {
      margin-top: 0.25rem;
    }
    .badged {
      display: grid;
      gap: var(--bino-space-sm);
    }
    .badged-row {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .badged-row .path {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
    }
    .badged-row .why {
      flex: 1 1 24rem;
      min-width: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .row {
      display: flex;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .row bn-field {
      margin-bottom: 0;
    }
    .grow {
      flex: 1 1 16rem;
      min-width: 10rem;
    }
    input[type="file"] {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: var(--bino-space-xs) 0;
    }
    input[type="file"]:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .failure .actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .refs {
      margin: var(--bino-space-sm) 0 0;
      padding: 0;
      list-style: none;
    }
    .refs li {
      padding: var(--bino-space-xs) 0;
      border-bottom: 1px solid var(--bino-border);
      font-size: var(--bino-font-size-md);
    }
    .refs li:last-child {
      border-bottom: none;
    }
    .refs .obj {
      font-weight: 600;
    }
  `;

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.healthTimer);
  }

  /** The load is driven from `willUpdate` alone, which is this codebase's shape for
   * a property-driven page: the router sets `instanceId` before the first update, so
   * loading from `connectedCallback` as well would fetch everything twice on every
   * navigation. */
  override willUpdate(changed: PropertyValues<this>) {
    if (changed.has("instanceId") && this.instanceId) void this.load();
  }

  private async load() {
    this.loading = true;
    try {
      const listing = await listDatasources(this.instanceId);
      this.listing = listing;
      this.failure = null;
      this.scheduleHealth(listing.health.refresh_after_seconds);
      void this.loadFiles();
    } catch (err) {
      this.failure = parseError(err);
      this.listing = null;
    } finally {
      this.loading = false;
    }
  }

  /**
   * scheduleHealth re-reads the panel on the instance's OWN probe interval.
   * Polling faster would render a number that cannot change and would imply a
   * resolution the data does not have (`SC-S406`).
   */
  private scheduleHealth(seconds: number) {
    clearTimeout(this.healthTimer);
    const ms = Math.max(5, seconds || 30) * 1000;
    this.healthTimer = window.setTimeout(() => {
      if (!this.isConnected) return;
      void this.refreshQuietly();
    }, ms);
  }

  /** refreshQuietly re-reads without the loading skeleton, so a background health
   * poll does not blank the table the reader is looking at. */
  private async refreshQuietly() {
    try {
      const listing = await listDatasources(this.instanceId);
      this.listing = listing;
      this.failure = null;
      this.scheduleHealth(listing.health.refresh_after_seconds);
    } catch {
      // A failed background poll leaves the last reading on screen. Every row
      // carries its own `checked_at` and the panel carries `observed_at`, so the
      // reader can see how old it is; blanking the table would be worse.
      this.scheduleHealth(60);
    }
  }

  private async loadFiles() {
    try {
      this.files = await listFiles(this.instanceId);
      this.filesFailure = null;
    } catch (err) {
      this.files = null;
      this.filesFailure = parseError(err);
    }
  }

  /* ── validate configuration (`SC-F407`) ───────────────────────────── */

  private async runCheck() {
    this.checking = true;
    this.checkFailure = null;
    try {
      this.check = await checkDatasources(this.instanceId);
    } catch (err) {
      this.check = null;
      this.checkFailure = parseError(err);
    } finally {
      this.checking = false;
    }
  }

  /* ── the sample catalog (`SC-F416`) ───────────────────────────────── */

  private async attachSample() {
    this.sampleBusy = true;
    try {
      const result = await attachSampleCatalog(this.instanceId);
      toast(result.notice, "success", 12000);
      await this.load();
    } catch (err) {
      const failure = parseError(err);
      toast(failure.message, "error");
    } finally {
      this.sampleBusy = false;
    }
  }

  /* ── files (`SC-F415`) ────────────────────────────────────────────── */

  private onPickFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const picked = input.files?.[0] ?? null;
    this.uploadFile = picked;
    if (picked && this.uploadName === "") this.uploadName = picked.name;
  }

  private async upload() {
    const picked = this.uploadFile;
    if (!picked || this.uploadName === "") return;
    const replacing = (this.files?.items ?? []).some((f) => f.name === this.uploadName);
    this.uploading = true;
    try {
      const result = await placeFile(this.instanceId, this.uploadName, picked, {
        replace: replacing,
      });
      // The notice is the server's own sentence and it distinguishes the three
      // outcomes that matter: placed, replaced (still serving the old contents
      // until a restart), and byte-identical (no restart at all).
      toast(result.notice ?? "The file is placed.", result.restart_required ? "warning" : "success", 12000);
      this.uploadFile = null;
      this.uploadName = "";
      await this.loadFiles();
    } catch (err) {
      toast(parseError(err).message, "error");
    } finally {
      this.uploading = false;
    }
  }

  private async removeFile(name: string) {
    try {
      await deleteFile(this.instanceId, name);
      toast(`${name} is removed from the volume.`, "success");
      await this.loadFiles();
    } catch (err) {
      toast(parseError(err).message, "error");
    }
  }

  /* ── delete, guarded (`SC-F413`) ──────────────────────────────────── */

  private openDelete(ds: DataSource) {
    this.deleteTarget = ds;
    this.deleteFailure = null;
    this.references = [];
    this.forceAcknowledged = false;
  }

  private closeDelete() {
    this.deleteTarget = null;
    this.deleting = false;
  }

  private async confirmDelete(force: boolean) {
    const target = this.deleteTarget;
    if (!target) return;
    this.deleting = true;
    this.deleteFailure = null;
    try {
      if (target.sample) await removeSampleCatalog(this.instanceId, { force });
      else await deleteDatasource(this.instanceId, target.name, { force });
      toast(`${target.name} is removed from the draft. Publish to apply it.`, "success");
      this.closeDelete();
      await this.load();
    } catch (err) {
      const failure = parseError(err);
      this.deleteFailure = failure;
      // The guard's whole value is the list. `409` with
      // `details.reason = "catalog_referenced"` carries the referencing objects,
      // and without them the refusal is one a user cannot act on.
      const refs = errorDetail<CatalogReference[]>(failure, "references");
      this.references = Array.isArray(refs) ? refs : [];
      this.deleting = false;
    }
  }

  /* ── rendering ────────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private renderFailure(heading: string, err: ApiError, retry: () => void) {
    return html`<bn-card heading=${heading}>
      <div class="failure" role="alert">
        <p>${err.message}</p>
        <bn-kv .rows=${this.supportRows(err)}></bn-kv>
        ${err.requestId
          ? nothing
          : html`<p>
              This request did not reach Sluice Cloud, so there is no reference to quote. Check your
              connection and try again.
            </p>`}
        <div class="actions"><bn-button variant="primary" @click=${retry}>Try again</bn-button></div>
      </div>
    </bn-card>`;
  }

  /**
   * The restart-class statement, rendered **where the change is made** rather
   * than only in the publish dialog (`SC-F409`, `SC-S404`). It is permanent and
   * says so: `datasources.reload: true` is rejected by `sluice config validate`
   * and again at boot, so no upstream release softens this row.
   */
  private renderRestartNotice() {
    return html`<bn-panel eyebrow="Every datasource change restarts this instance">
      <p class="copy">
        Adding, changing or removing a datasource is a <strong>restart-class</strong> change. A
        catalog is attached only while the Sluice process starts, and
        <code>datasources.reload: true</code> is rejected both by <code>sluice config validate</code>
        and at boot — so this is permanent, not a limitation the next upstream release removes.
      </p>
      <p class="copy">
        Editing here changes the <strong>draft</strong> only; nothing reaches the instance until you
        publish. When you do, the instance is replaced: expect roughly 10 to 25 seconds during which
        queries fail, and DuckDB re-attaches and re-warms the catalogs afterwards. A policy publish
        costs no restart at all — the two are deliberately not the same action.
      </p>
      <div class="actions" style="margin-top: var(--bino-space-sm)">
        <bn-button size="sm" @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}>
          Review and publish
        </bn-button>
      </div>
    </bn-panel>`;
  }

  /** The health panel's summary line. A failed admin call says so **with** the
   * HTTP status, and the rows below stay visible marked stale rather than being
   * presented as current (`SC-S406`). */
  private renderHealthSummary() {
    const panel = this.listing?.health;
    if (!panel) return nothing;
    const meta = HEALTH[panel.overall] ?? { label: panel.overall, variant: "neutral" as BnBadgeVariant };
    return html`<bn-panel eyebrow="Catalog health">
      <div class="badged-row">
        <bn-badge .variant=${meta.variant}>${meta.label}</bn-badge>
        <span class="meta">
          Read ${when(panel.observed_at)} · the instance probes every
          ${panel.refresh_after_seconds}s with a ${panel.probe_timeout_seconds}s timeout, and this
          page refreshes on the same interval.
        </span>
      </div>
      ${panel.unavailable
        ? html`<p class="copy" style="margin-top: var(--bino-space-sm)">
            <strong>Health unavailable</strong> — ${panel.unavailable.message}${panel.unavailable
              .status
              ? html` The instance's admin plane answered HTTP ${panel.unavailable.status}.`
              : nothing}
            ${panel.unavailable.request_id
              ? html`<span class="mono">Admin request id ${panel.unavailable.request_id}</span>`
              : nothing}
            Any rows below are the control plane's last known readings, each marked stale with its
            own timestamp.
          </p>`
        : nothing}
    </bn-panel>`;
  }

  /** The schema cache's freshness (`SC-F411`). Stated, never implied: a stale or
   * empty cache is what makes a completion field fall back to free text. */
  private renderSchemaStatus() {
    const status = this.listing?.schema;
    if (!status) return nothing;
    if (!status.degraded && status.fresh && !status.truncated) {
      return html`<p class="meta">
        Schema cache: fresh, read ${when(status.fetched_at ?? "")}${status.object_count
          ? html` · ${status.object_count} objects`
          : nothing}.
      </p>`;
    }
    return html`<bn-panel eyebrow="Schema cache">
      <p class="copy">
        ${status.warning ??
        "The introspection cache is empty or stale, so the policy editors' table and column completions fall back to free text."}
        ${status.fetched_at
          ? html` Last read ${when(status.fetched_at)}.`
          : html` Nothing has been read from this instance yet.`}
        ${status.truncated
          ? html` The stored tree hit its cap, so completions are incomplete — a column the widget
            does not offer may still exist.`
          : nothing}
      </p>
    </bn-panel>`;
  }

  /** The badged not-enforced fields (`SC-F407`, `SC-F505`). They are listed with
   * the schema's own reason and its upstream citation, so a reader can check the
   * claim rather than trust it. */
  private renderNotEnforced(fields: NotEnforcedField[]) {
    if (fields.length === 0) return nothing;
    return html`<bn-card heading="Fields this build carries and does not act on">
      <p class="copy">
        These fields decode and are stored, and no code path in the pinned Sluice build reads them.
        The forms show them disabled with the reason rather than hiding them: a field you cannot see
        cannot teach you that the control you were looking for does nothing.
      </p>
      <div class="badged">
        ${fields.map(
          (f) => html`<div class="badged-row">
            <span class="path">${f.path}</span>
            <bn-badge variant="not-enforced">${f.badge || "not enforced in this build"}</bn-badge>
            <span class="why">
              ${f.reason}${f.source ? html` <span class="mono">(${f.source})</span>` : nothing}
            </span>
          </div>`,
        )}
      </div>
    </bn-card>`;
  }

  /**
   * The check result (`SC-F407`). The `not_checked` list and the notice are the
   * point of the panel: an `ok` here means every declared type has a driver in
   * this build, and nothing whatsoever about the network.
   */
  private renderCheck() {
    if (this.checkFailure) {
      return this.renderFailure("Configuration validation could not run", this.checkFailure, () =>
        void this.runCheck(),
      );
    }
    const report = this.check;
    if (!report) return nothing;
    return html`<bn-card heading="Validate configuration">
      <div class="badged-row">
        <bn-badge .variant=${report.ok ? "success" : "error"}>
          ${report.ok ? "Every declared type has a driver" : `${report.failed} declared type(s) have no driver`}
        </bn-badge>
        <span class="meta">${report.checked}</span>
      </div>
      <p class="copy" style="margin-top: var(--bino-space-sm)"><strong>${report.notice}</strong></p>
      <h2 style="margin-top: var(--bino-space-md)">Not checked</h2>
      <ul class="plain">
        ${report.not_checked.map((n) => html`<li>${n}</li>`)}
      </ul>
      ${report.datasources.length > 0
        ? html`<div style="margin-top: var(--bino-space-md)">
            <bn-table
              caption="Configuration validation results"
              .columns=${[
                { key: "name", label: "Catalog" },
                { key: "type", label: "Declared type", width: "1px" },
                { key: "factory", label: "Driver", width: "1px" },
                { key: "filters", label: "Declared filters", width: "1px" },
              ] as BnColumn[]}
              .rows=${report.datasources.map((row) => ({
                name: row.name,
                type: html`<span class="mono">${row.type}</span>`,
                factory: row.factory_known
                  ? html`<bn-badge variant="success">registered</bn-badge>`
                  : html`<bn-badge variant="error">unknown</bn-badge>`,
                filters:
                  row.schemas + row.tables === 0
                    ? html`<span class="sub">none</span>`
                    : html`<span class="sub"
                        >${row.schemas} schema(s), ${row.tables} table(s) — carried, read by no
                        driver</span
                      >`,
              })) as BnRow[]}
            ></bn-table>
            ${report.datasources
              .filter((row) => row.error)
              .map(
                (row) => html`<p class="driver-error">${row.name}: ${row.error}</p>`,
              )}
          </div>`
        : nothing}
      ${report.not_enforced.length > 0
        ? html`<p class="meta" style="margin-top: var(--bino-space-sm)">
            Spec fields that parse and do nothing in this build:
            ${report.not_enforced.join(", ")}.
          </p>`
        : nothing}
    </bn-card>`;
  }

  /** One health cell: the status, when the instance last probed, the latency, and
   * the driver's own error verbatim in a copyable block. */
  private renderHealthCell(ds: DataSource) {
    const health: Health | undefined = ds.health;
    if (!health) {
      return html`<bn-badge variant="neutral">Unknown</bn-badge>
        <span class="sub">never probed</span>`;
    }
    const meta = HEALTH[health.status] ?? { label: health.status, variant: "neutral" as BnBadgeVariant };
    return html`<bn-badge .variant=${meta.variant}>${meta.label}</bn-badge>
      <span class="sub">
        ${health.checked_at ? `checked ${when(health.checked_at)}` : "never probed"}
        ${health.latency_ms !== undefined ? ` · ${health.latency_ms} ms` : ""}
        ${health.stale ? " · last known reading" : ""}
      </span>
      ${health.error ? html`<p class="driver-error">${health.error}</p>` : nothing}`;
  }

  /** The target cell: what the driver actually opens. A DSN is shown without any
   * credential, because the API never returns one. */
  private renderTarget(ds: DataSource) {
    const parts: string[] = [];
    if (ds.connection) parts.push(ds.connection);
    if (ds.path) parts.push(ds.path);
    if (ds.bucket) parts.push(`s3://${ds.bucket}${ds.prefix ? `/${ds.prefix}` : ""}`);
    if (ds.database) parts.push(ds.database);
    if (ds.endpoint) parts.push(ds.endpoint);
    return html`<span class="mono">${parts.join(" · ") || "—"}</span>
      ${ds.readonly
        ? nothing
        : html`<span class="sub">attached writable — a widening setting</span>`}
      ${ds.allowed_paths && ds.allowed_paths.length > 0
        ? html`<span class="sub">allowed paths: ${ds.allowed_paths.join(", ")}</span>`
        : nothing}`;
  }

  private toRows(): BnRow[] {
    return (this.listing?.items ?? []).map((ds) => ({
      catalog: html`<a class="name" href=${hrefFor("instances", this.instanceId, "datasources", ds.name)}>
          ${ds.name}
        </a>
        ${ds.sample ? html`<bn-badge variant="info">sample data</bn-badge>` : nothing}
        ${ds.display_name ? html`<span class="sub">${ds.display_name}</span>` : nothing}`,
      type: html`<span class="mono">${ds.type}</span>`,
      health: this.renderHealthCell(ds),
      target: this.renderTarget(ds),
      // The redacted state, not a masked value: the credential exists and is
      // never sent to the browser, and bn-kv's redacted row has no copy button
      // because there is nothing to copy (`SC-F405`, `SC-S409`).
      credential: ds.has_credential
        ? html`<bn-kv
            .rows=${[{ label: "Credential", redacted: true }] as BnKvRow[]}
            redactedText="set · never shown"
            redactedHint="Entered separately from the DSN, sealed at rest and materialized as a file inside the container. It never round-trips to the browser after save."
          ></bn-kv>`
        : html`<span class="sub">${ds.path ? "not needed" : "none"}</span>`,
      actions: html`<span class="cell-actions">
        <bn-button
          size="sm"
          @click=${() => navigate(hrefFor("instances", this.instanceId, "datasources", ds.name))}
        >
          Edit
        </bn-button>
        <bn-button size="sm" variant="danger" @click=${() => this.openDelete(ds)}>Remove</bn-button>
      </span>`,
    }));
  }

  private renderEmpty() {
    return html`<bn-empty-state
      icon="⛁"
      heading="No datasources yet"
      message="A datasource is one catalog this instance attaches: a database, a file on its volume, or a bucket of Parquet. Its name becomes the DuckDB catalog name that every policy's resources.catalogs pattern matches, so it is worth choosing deliberately."
    >
      <bn-button
        slot="actions"
        variant="primary"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "datasources", "new"))}
      >
        Add a datasource
      </bn-button>
      <bn-button slot="actions" ?loading=${this.sampleBusy} @click=${() => void this.attachSample()}>
        Attach the sample catalog
      </bn-button>
    </bn-empty-state>`;
  }

  /** The sample catalog card (`SC-F416`): one action to attach, one to remove,
   * and the honest statement that it becomes queryable only after a restart. */
  private renderSample() {
    const sample = (this.listing?.items ?? []).find((ds) => ds.sample);
    if (sample) {
      return html`<bn-card heading="Sample catalog">
        <p class="copy">
          <strong>${sample.name}</strong> is attached and badged as sample data on every surface that
          shows it. It is an ordinary <code>DataSource</code> manifest — you can read its generated
          YAML, edit it and delete it — and removing it also removes its file from the volume.
        </p>
        <bn-button
          ?loading=${this.sampleBusy}
          @click=${() => this.openDelete(sample)}
        >
          Remove the sample catalog
        </bn-button>
      </bn-card>`;
    }
    return html`<bn-card heading="Sample catalog">
      <p class="copy">
        A synthetic read-only dataset with obviously fake personal data, placed on this instance's own
        volume. It needs no firewall change and no egress from the instance, so the policy editors,
        the pattern preview, <code>policy explain</code> and the playground can all be exercised
        before you connect a real database. It becomes queryable when the instance next starts,
        because attaching a catalog is a restart.
      </p>
      <bn-button ?loading=${this.sampleBusy} @click=${() => void this.attachSample()}>
        Attach the sample catalog
      </bn-button>
    </bn-card>`;
  }

  /** The files card (`SC-F415`). Without this path `sqlite` and `duckdb_file` are
   * configurable and impossible to make work: the volume is node-local and the
   * customer has no shell. */
  private renderFiles() {
    if (this.filesFailure) {
      return this.renderFailure("Could not read the state volume", this.filesFailure, () =>
        void this.loadFiles(),
      );
    }
    const listing = this.files;
    if (!listing) return nothing;
    const rows: BnRow[] = listing.items.map((f) => ({
      name: html`<span class="mono">${f.name}</span>
        ${f.sample ? html`<bn-badge variant="info">sample data</bn-badge>` : nothing}
        <span class="sub">${f.path}</span>`,
      size: bytes(f.size_bytes),
      digest: html`<span class="mono">${f.digest.slice(0, 16)}…</span>`,
      used:
        f.used_by && f.used_by.length > 0
          ? html`<span class="sub">${f.used_by.join(", ")}</span>`
          : html`<span class="sub">nothing</span>`,
      actions: html`<span class="cell-actions">
        <bn-button size="sm" variant="danger" @click=${() => void this.removeFile(f.name)}>
          Delete
        </bn-button>
      </span>`,
    }));
    return html`<bn-card heading="Files on the state volume">
      <p class="copy">
        A <code>sqlite</code> or <code>duckdb_file</code> datasource reads a file from
        <code>${listing.root}</code> on this instance's own volume. Uploads run as a job through the
        same node-pinned one-shot writer the publish path uses — there is no shell and no standing
        agent. This instance accepts up to ${bytes(listing.max_bytes)} per file, and you name the
        file, never the directory.
      </p>
      <p class="copy">
        Replacing a file in place is <strong>restart-class</strong>: DuckDB attaches it at process
        start, so until the instance restarts it keeps serving the previous contents.
      </p>
      <div class="row" style="margin: var(--bino-space-md) 0">
        <bn-field class="grow" label="File to upload">
          <input
            type="file"
            aria-label="File to upload"
            @change=${(e: Event) => this.onPickFile(e)}
          />
        </bn-field>
        <bn-field
          class="grow"
          label="Name on the volume"
          hint="You name the file, never the directory: it lands directly under ${listing.root}"
        >
          <bn-input
            mono
            .value=${this.uploadName}
            @input=${(e: CustomEvent<{ value: string }>) => (this.uploadName = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-button
          variant="primary"
          ?disabled=${!this.uploadFile || this.uploadName === ""}
          ?loading=${this.uploading}
          @click=${() => void this.upload()}
        >
          Place the file
        </bn-button>
      </div>
      ${listing.items.length === 0
        ? html`<p class="meta">No files are placed on this volume yet.</p>`
        : html`<bn-table
            caption="Files on the state volume"
            .columns=${FILE_COLUMNS}
            .rows=${rows}
          ></bn-table>`}
    </bn-card>`;
  }

  /**
   * The delete ceremony (`SC-F413`, `SC-S408`). The refusal lists the referencing
   * objects with the pattern that matched, because an unknown catalog pattern in
   * Sluice does not error — it simply never matches, so an unchecked delete leaves
   * protection that appears configured and is inert.
   */
  private renderDeleteModal() {
    const target = this.deleteTarget;
    if (!target) return nothing;
    const blocked = this.references.length > 0;
    return html`<bn-modal
      open
      tone="destructive"
      .width=${620}
      heading=${`Remove ${target.name}?`}
      @close=${() => this.closeDelete()}
    >
      <p class="copy">
        This removes the catalog from the <strong>draft</strong>. Nothing changes on the instance
        until you publish, and publishing it is a restart.
      </p>
      ${target.sample
        ? html`<p class="copy">
            The sample catalog's file is removed from the volume in the same action.
          </p>`
        : nothing}
      ${blocked
        ? html`<h2 style="margin-top: var(--bino-space-md)">
              ${this.references.length} policy object(s) reference this catalog
            </h2>
            <ul class="refs">
              ${this.references.map(
                (ref) => html`<li>
                  <a
                    class="name"
                    href=${hrefFor("instances", this.instanceId, "policies", String(ref.kind), ref.name)}
                  >
                    <span class="obj">${ref.kind}/${ref.name}</span>
                  </a>
                  <span class="sub">
                    ${ref.wildcard ? "pattern" : "names the catalog"}:
                    <span class="mono">${ref.pattern}</span>
                    ${ref.path ? html` at <span class="mono">${ref.path}</span>` : nothing}
                  </span>
                </li>`,
              )}
            </ul>`
        : nothing}
      ${this.deleteFailure && !blocked
        ? html`<p class="copy" role="alert">
            ${this.deleteFailure.message}
            ${this.deleteFailure.requestId
              ? html`<span class="mono">Reference ${this.deleteFailure.requestId}</span>`
              : nothing}
          </p>`
        : nothing}
      ${blocked
        ? html`<div slot="consequence">
            <p style="margin: 0 0 var(--bino-space-sm)">
              Removing it anyway does not break those policies — they still compile. They stop
              matching, silently, and protect nothing.
            </p>
            <bn-checkbox
              label="I understand the referencing policies will silently match nothing"
              hint="This is the exact acknowledgement the API requires (acknowledge=${FORCE_ACKNOWLEDGEMENT})."
              .checked=${this.forceAcknowledged}
              @change=${(e: CustomEvent<{ checked: boolean }>) =>
                (this.forceAcknowledged = e.detail.checked)}
            ></bn-checkbox>
          </div>`
        : nothing}
      <bn-button slot="footer" @click=${() => this.closeDelete()}>Cancel</bn-button>
      ${blocked
        ? html`<bn-button
            slot="footer"
            variant="danger"
            ?disabled=${!this.forceAcknowledged}
            ?loading=${this.deleting}
            @click=${() => void this.confirmDelete(true)}
          >
            Remove anyway
          </bn-button>`
        : html`<bn-button
            slot="footer"
            variant="danger"
            ?loading=${this.deleting}
            @click=${() => void this.confirmDelete(false)}
          >
            Remove from the draft
          </bn-button>`}
    </bn-modal>`;
  }

  override render() {
    if (this.loading && !this.listing) {
      return html`<bn-spinner block size="28" label="Loading datasources"></bn-spinner>`;
    }
    if (this.failure) {
      return html`<a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
        ${this.renderFailure("Could not load datasources", this.failure, () => void this.load())}`;
    }
    const listing = this.listing;
    const items = listing?.items ?? [];
    const notEnforced = listing?.not_enforced ?? [];
    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <div class="head-text">
          <h1>Datasources</h1>
          <p class="lede">
            Each row is one catalog this instance attaches. The name is the DuckDB catalog name that
            every policy's <code>resources.catalogs</code> pattern matches — renaming one is a
            separate, guarded operation, because the policies would still compile and simply stop
            matching.
          </p>
        </div>
        <div class="head-actions">
          <bn-button ?loading=${this.checking} @click=${() => void this.runCheck()}>
            Validate configuration
          </bn-button>
          <bn-button
            variant="primary"
            @click=${() => navigate(hrefFor("instances", this.instanceId, "datasources", "new"))}
          >
            Add datasource
          </bn-button>
        </div>
      </header>

      <div class="stack">
        ${this.renderRestartNotice()} ${this.renderHealthSummary()} ${this.renderCheck()}
        <bn-card flush>
          <bn-table
            caption="Datasources"
            .columns=${COLUMNS}
            .rows=${this.toRows()}
            ?loading=${this.loading}
          >
            <div slot="empty">${this.renderEmpty()}</div>
          </bn-table>
        </bn-card>
        ${items.length > 0 ? this.renderSchemaStatus() : nothing}
        ${items.length > 0
          ? html`<schema-browser .instanceId=${this.instanceId} canRefresh></schema-browser>`
          : nothing}
        ${this.renderSample()} ${this.renderFiles()} ${this.renderNotEnforced(notEnforced)}
      </div>
      ${this.renderDeleteModal()}
    `;
  }
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date" if the server sends something this bundle cannot
 * parse. */
function when(iso: string): string {
  if (!iso) return "never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** bytes renders a size the way a reader checking an upload limit reads one. */
function bytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KiB", "MiB", "GiB"];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

declare global {
  interface HTMLElementTagNameMap {
    "page-datasources": PageDatasources;
  }
}
